import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Chip from '@mui/material/Chip';

const ModernAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(11, 14, 17, 0.95) 0%, rgba(22, 26, 30, 0.98) 100%)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid var(--border-primary)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  zIndex: 1300
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  padding: '8px 0',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)'
  }
}));

const BrandText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Playfair Display, serif',
  fontWeight: 700,
  fontSize: '24px',
  background: 'linear-gradient(135deg, #7ead49 0%, #a4d470 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const NavSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginLeft: 'auto'
}));

const MarketTicker = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 12px',
  background: 'rgba(247, 147, 26, 0.1)',
  borderRadius: '8px',
  border: '1px solid rgba(247, 147, 26, 0.2)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const StatusIndicator = styled(Box)(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: '#02C076',
  boxShadow: '0 0 8px rgba(2, 192, 118, 0.6)',
  animation: 'pulse 2s infinite'
}));

const PriceDisplay = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  color: '#02C076'
}));

const NotificationButton = styled(IconButton)(({ theme }) => ({
  background: 'transparent',
  width: '44px',
  height: '44px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)'
  }
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
  width: '44px',
  height: '44px',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(247, 147, 26, 0.4)'
  }
}));

export default function Header() {
  const appMode = useSelector((state) => state.mode.mode);
  const notifications = useSelector((state) => state.notifications.notifications);
  const user = useSelector((state) => state.user.user);
  const coinRates = useSelector((state) => state.coins.coinRates);

  const dispatch = useDispatch();

  const toggleSidebar = () => {
    const event = new Event("toggleSideBar");
    window.dispatchEvent(event);
  }

  // Get BTC price for header ticker
  const btcPrice = coinRates?.find(coin => coin.symbol === 'BTC')?.priceUsd || '0';
  const btcChange = coinRates?.find(coin => coin.symbol === 'BTC')?.changePercent24Hr || '0';
  const isPositive = parseFloat(btcChange) >= 0;

  return (
    <Box sx={{ flexGrow: 1, position: 'fixed', zIndex: 1300, width: '100%' }}>
      <ModernAppBar position="static" elevation={0}>
        <Toolbar sx={{ padding: { xs: '0 16px', md: '0 24px' }, minHeight: '70px' }}>
          
          {/* Logo Section */}
          <LogoContainer component={RouterLink} to="/">
            <Box
              component="img"
              src="/logos/altairfinance-main-logo.png"
              alt="ALTAIR FINANCE Logo"
              sx={{ 
                height: '30px', 
                filter: 'drop-shadow(0 2px 8px rgba(126, 173, 73, 0.3))',
                transition: 'all 0.3s ease'
              }}
            />
          </LogoContainer>

          {/* Market Ticker */}
          {/* Removed BTC price ticker */}

          {/* Navigation Section */}
          <NavSection>
            
            {/* Notifications */}
            <NotificationButton
              component={RouterLink}
              to="/notifications"
              size="large"
              aria-label="notifications"
            >
              <Badge 
                badgeContent={notifications?.length || 0} 
                sx={{
                  '& .MuiBadge-badge': {
                    background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 600
                  }
                }}
              >
                <NotificationsIcon sx={{ color: '#FFFFFF', fontSize: '20px' }} />
              </Badge>
            </NotificationButton>
            
            {/* Menu Button for Mobile */}
          <IconButton
            onClick={toggleSidebar}
            size="large"
            edge="start"
              aria-label="menu"
              sx={{ 
                display: { md: 'none' },
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <MenuIcon sx={{ fontSize: '24px' }} />
          </IconButton>
          </NavSection>
        </Toolbar>
      </ModernAppBar>
    </Box>
  );
}